import { MachineConfig, assign, Sender, DoneInvokeEvent } from 'xstate';
import { log } from 'xstate/lib/actions';
import { SetupIntent } from '@stripe/stripe-js';
import { FormValues } from '../../components/case/CaseForm';
import { Case } from '../../services/buildfire/rdb/cases';
import * as API from '../../api/cloudFunctions';
import { getFunctions } from 'firebase/functions';
import { FirebaseApp } from 'firebase/app';
import { PaymentType } from './selectPaymentType';
import { CaseType } from './caseType';
import { assignResult } from '../util/actionHelpers';

export interface CheckoutContext {
  firebaseApp: FirebaseApp;
  caseInfo: FormValues;
  userId: string;
  email: string;
  name: string;
  createdCase?: Case;
  setupIntent: SetupIntent;
  selectedPaymentMethod: string;
  error?: string | Error | API.FirebaseFunctionsError;
  uploadProgress?: {
    percent: number;
    message: string;
  };
  selectedInvoiceAccountUid: string;
  paymentType: PaymentType;
  parentAccount?: API.ParentUser;
  caseType: CaseType;
  lineItems?: API.PrecheckoutResult['lineItems'];
  totalPrice?: number; // in cents
  priorityUpgradePrice?: number; // in cents
}

export type CheckoutEvent =
  | { type: 'NEXT' }
  | { type: 'BACK' }
  | { type: 'SET_UPLOAD_PROGRESS'; data: { percent: number; message: string } };

function calculatePercent(num: number, total: number) {
  return Math.min((num + 1) / total, 0.99);
}

async function confirmSetupIntent(context: CheckoutContext) {
  const functions = getFunctions(context.firebaseApp);
  const setupIntentId =
    context.setupIntent?.payment_method === context.selectedPaymentMethod
      ? context.setupIntent?.id
      : undefined;
  const result = await API.setupIntent(functions, {
    userId: context.userId,
    email: context.email,
    name: context.name,
    confirm: {
      setupIntentId,
      paymentMethodId: context.selectedPaymentMethod!,
    },
  });

  return result.setupIntent;
}

async function precheckout(context: CheckoutContext) {
  const { firebaseApp, caseInfo, userId } = context;

  const functions = getFunctions(firebaseApp);
  const result = await API.precheckout(functions, {
    sites: caseInfo.sites.map((site) => ({
      location: site.location,
      description: site.description,
      additionalInfo: site.additionalInfo,
    })),
    userId,
    priority: context.caseType === CaseType.PRIORITY,
  });

  return {
    lineItems: result.lineItems,
    totalPrice: result.total,
    priorityUpgradePrice: result.priorityUpgradePrice,
    error: null,
  };
}

function submitCase({
  caseInfo,
  userId,
  name,
  email,
  selectedPaymentMethod,
  setupIntent,
  firebaseApp,
  paymentType,
  selectedInvoiceAccountUid,
  caseType,
}: CheckoutContext) {
  return async (send: Sender<CheckoutEvent>) => {
    try {
      send({
        type: 'SET_UPLOAD_PROGRESS',
        data: {
          message: `Submitting case...`,
          percent: 0.5,
        },
      });

      const {
        submitterEmailAddresses,
        submitterFirstName,
        submitterLastName,
        patientFirstName,
        patientLastName,
        sites,
        ...rest
      } = caseInfo;

      const emailAddresses = submitterEmailAddresses?.filter(Boolean);

      const invoicingEnabled = paymentType === PaymentType.INVOICE;
      const functions = getFunctions(firebaseApp);

      send({
        type: 'SET_UPLOAD_PROGRESS',
        data: {
          message: `Finalizing submission...`,
          percent: 0.9,
        },
      });

      const result = await API.submitCase(functions, {
        case: {
          ...rest,
          priority: caseType === CaseType.PRIORITY,
          // Sites should already be uploaded from draft saving
          sites,
          user: {
            email,
            displayName: name,
          },
          submitter: {
            first: submitterFirstName,
            last: submitterLastName,
            email: emailAddresses[0], // TODO deprecate and only use `additionalEmails`
            additionalEmails: emailAddresses?.slice(1),
          },
          patient: {
            first: patientFirstName,
            last: patientLastName,
          },
          uid: userId,
          billToParentAccount:
            invoicingEnabled && selectedInvoiceAccountUid !== userId,
        },
        user: {
          id: userId,
          name,
          email,
        },
        paymentMethodId: invoicingEnabled ? undefined : selectedPaymentMethod,
        setupIntentId: invoicingEnabled ? undefined : setupIntent.id,
        invoiceAccount: invoicingEnabled
          ? selectedInvoiceAccountUid
          : undefined,
      });

      return result;
    } catch (e: any) {
      console.error(e);
      throw new Error(
        e?.error?.message ||
          e?.error?.status ||
          e?.error ||
          e?.message ||
          'Error submitting case, please try again'
      );
    }
  };
}

const checkoutStates: MachineConfig<CheckoutContext, any, CheckoutEvent> = {
  initial: 'ensureSetupIntent',
  states: {
    ensureSetupIntent: {
      always: [
        {
          target: 'load',
          cond: (context) =>
            context.paymentType === PaymentType.INVOICE || !!context.setupIntent,
        },
        {
          target: 'confirmingSetupIntent',
        },
      ],
    },
    confirmingSetupIntent: {
      invoke: {
        src: confirmSetupIntent,
        onDone: {
          target: 'load',
          actions: assign({
            setupIntent: (context, ev) => ev.data,
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (context, ev) => ev.data,
          }),
        },
      },
    },
    load: {
      invoke: {
        src: precheckout,
        onDone: {
          target: 'idle',
          actions: assignResult,
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (context, ev) => ev.data,
          }),
        },
      },
    },
    idle: {
      on: {
        NEXT: 'submitCase',
      },
    },
    submitCase: {
      on: {
        SET_UPLOAD_PROGRESS: {
          actions: [
            log((context, ev) => {
              console.log(ev);
            }),
            assign({
              uploadProgress: (c, { data }) => data,
            }),
          ],
        },
      },
      invoke: {
        src: submitCase,
        onDone: {
          target: 'success',
          actions: assign({
            createdCase: (context, ev) => ev.data.case,
          }),
        },
        onError: {
          target: 'idle',
          actions: [
            log((context, ev) => {
              console.error(ev);
            }),
            assign({
              error: (context, ev) => ev.data,
            }),
          ],
        },
      },
    },
    error: {
      always: {
        target: 'idle',
        actions: log((context, ev) => {
          console.error(ev);
        }),
      },
    },
    success: {
      type: 'final',
    },
  },
};

export default checkoutStates;
